import {
  useGetAlphaBscWalletQuery,
  useGetAlphaBscTokensQuery,
  useGetAlphaBscPositionsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { fmtPrice } from "@/lib/format";
import { AlphaBscWsAlertsCard } from "@/components/domain/AlphaBscWsAlertsCard";
import { ConvertHistoryCard } from "@/components/domain/ConvertHistoryCard";
import { Web3WalletExitCard } from "@/components/domain/Web3WalletExitCard";

/**
 * Alpha BSC Overview — KPI dashboard for the entire BSC alpha system.
 *
 * Aggregates state from Phases 1/2/3:
 *   - Wallet status (address, BNB, USDT)
 *   - Watchlist count + top graduation scores
 *   - Open positions: count, total invested, current value
 *   - Closed positions: total realized P&L, win rate
 *   - Phase enablement indicators
 */
export default function AlphaBscOverview() {
  const wallet = useGetAlphaBscWalletQuery(undefined, { pollingInterval: 30000 });
  const watching = useGetAlphaBscTokensQuery("watching", { pollingInterval: 60000 });
  const openPos = useGetAlphaBscPositionsQuery("open", { pollingInterval: 30000 });
  const closedPos = useGetAlphaBscPositionsQuery("closed", { pollingInterval: 60000 });

  const w = wallet.data || {};
  const configured = w.configured === true;

  const totalInvested = (openPos.data || []).reduce(
    (sum: number, p: any) => sum + Number(p.entry_size_usdt || 0), 0);
  const totalUnrealizedPnl = (openPos.data || []).reduce(
    (sum: number, p: any) => sum + Number(p.unrealized_pnl_usd ?? 0), 0);
  const totalCurrentValue = (openPos.data || []).reduce(
    (sum: number, p: any) => sum + Number(p.current_value_usdt ?? p.entry_size_usdt ?? 0), 0);
  const totalRealizedPnl = (closedPos.data || []).reduce(
    (sum: number, p: any) => sum + Number(p.realized_pnl_usd || 0), 0);
  const closedWinCount = (closedPos.data || []).filter(
    (p: any) => Number(p.realized_pnl_usd || 0) > 0).length;
  const winRate = closedPos.data && closedPos.data.length > 0
    ? (closedWinCount / closedPos.data.length) * 100
    : 0;
  const autoPosCount = (openPos.data || []).filter(
    (p: any) => p.auto_managed === 1).length;
  const manualPosCount = (openPos.data || []).filter(
    (p: any) => !p.auto_managed).length;

  // Top 5 watchlist by score
  const topWatched = [...(watching.data || [])]
    .sort((a: any, b: any) => (b.graduation_score || 0) - (a.graduation_score || 0))
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Alpha BSC</h1>
        <p className="text-sm text-muted">
          On-chain trading of Binance Alpha tokens via PancakeSwap + Uniswap V3 (BSC).
          Discover → buy → manage → exit on graduation. Real funds, bounded risk.
        </p>
      </header>

      {/* KPI ROW — 5 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          label="Wallet USDT"
          value={configured ? `$${Number(w.usdt_balance?.formatted ?? 0).toFixed(2)}` : "—"}
          sub={configured ? `${Number(w.bnb_balance ?? 0).toFixed(4)} BNB` : "Wallet not set"}
          tone={configured ? (w.bnb_balance < 0.005 ? "warn" : "good") : "bad"}
        />
        <KpiCard
          label="Watching"
          value={String(watching.data?.length ?? 0)}
          sub={topWatched.length > 0 ? `Top: ${topWatched[0].symbol} (${topWatched[0].graduation_score?.toFixed(0)})` : "No tokens yet"}
        />
        <KpiCard
          label="Open positions"
          value={String(openPos.data?.length ?? 0)}
          sub={`$${totalInvested.toFixed(2)} invested · ${autoPosCount} auto, ${manualPosCount} manual`}
          tone={openPos.data?.length ? "info" : "muted"}
        />
        <KpiCard
          label="Unrealized P&L"
          value={`${totalUnrealizedPnl >= 0 ? "+" : ""}$${totalUnrealizedPnl.toFixed(4)}`}
          sub={`Current value: $${totalCurrentValue.toFixed(2)}`}
          tone={totalUnrealizedPnl > 0.01 ? "good" : totalUnrealizedPnl < -0.01 ? "bad" : "muted"}
        />
        <KpiCard
          label="Realized P&L"
          value={`${totalRealizedPnl >= 0 ? "+" : ""}$${totalRealizedPnl.toFixed(4)}`}
          sub={closedPos.data?.length ? `${closedPos.data.length} closed · ${winRate.toFixed(0)}% wins` : "No exits yet"}
          tone={totalRealizedPnl > 0 ? "good" : totalRealizedPnl < 0 ? "bad" : "muted"}
        />
      </div>

      {/* PHASE STATUS */}
      <Card>
        <CardHeader>
          <CardTitle>System status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <PhaseCard
              phase="Phase 1 — Data layer"
              status="ON"
              tone="good"
              detail="GeckoTerminal pool monitoring, 15min refresh, graduation detection. Always armed."
            />
            <PhaseCard
              phase="Phase 2 — Manual trading"
              status={configured ? "ON" : "Wallet not set"}
              tone={configured ? "good" : "warn"}
              detail="On-chain swap via PancakeSwap V3 + Uniswap V3 BSC. $25/trade default, $100 daily cap."
            />
            <PhaseCard
              phase="Phase 3 — Auto-strategy"
              status="Check Auto tab"
              tone="info"
              detail="Tier exits +30%/+60%/trail, hard stop -25%, graduation sell. 5min tick when enabled."
            />
          </div>
        </CardContent>
      </Card>

      {/* TOP WATCHLIST */}
      {topWatched.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top watchlist candidates by graduation score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topWatched.map((t: any) => (
                <div key={t.id} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">{t.symbol}</span>
                    <Badge tone={
                      t.graduation_score >= 70 ? "good"
                      : t.graduation_score >= 50 ? "warn"
                      : "muted"
                    }>
                      score {Number(t.graduation_score).toFixed(0)}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted tabular-nums">
                    {fmtPrice(Number(t.price_usd ?? 0))} ·
                    vol ${Number(t.volume_24h_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})} ·
                    liq ${Number(t.liquidity_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-muted">
              <Link to="/alpha-bsc/watchlist" className="hover:underline">View all watched →</Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* QUICK LINKS */}
      <Card>
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <QuickLink to="/alpha-bsc/watchlist" label="Manage watchlist" desc="Add tokens by symbol or 0x address" />
            <QuickLink to="/alpha-bsc/manual" label="Buy / sell" desc="Manual trade with size override" />
            <QuickLink to="/alpha-bsc/auto" label="Auto-strategy" desc="Tier exits + graduation sell" />
            <QuickLink to="/alpha-bsc/history" label="Closed positions" desc="Realized P&L, exit reasons" />
          </div>
        </CardContent>
      </Card>

      {wallet.isLoading && <Skeleton className="h-24 w-full" />}

      {/* Primary path for STF-blocked exits — operator uses Binance Web3 Wallet */}
      <Web3WalletExitCard />

      {/* Real-time spike alerts from the Binance Alpha WS watcher */}
      <AlphaBscWsAlertsCard />

      {/* Binance Convert — secondary path, only useful when the asset is on Spot */}
      <ConvertHistoryCard />
    </div>
  );
}

function KpiCard({ label, value, sub, tone }: {
  label: string; value: string; sub?: string;
  tone?: "good" | "bad" | "warn" | "info" | "muted";
}) {
  const tones: Record<string, string> = {
    good: "text-green-500", bad: "text-red-500",
    warn: "text-yellow-500", info: "", muted: "",
  };
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted">{label}</div>
        <div className={`text-xl font-semibold tabular-nums ${tones[tone ?? "info"]}`}>{value}</div>
        {sub && <div className="text-[11px] text-muted mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function PhaseCard({ phase, status, tone, detail }: {
  phase: string; status: string;
  tone: "good" | "warn" | "info" | "muted";
  detail: string;
}) {
  return (
    <div className="rounded border p-3 bg-muted/5">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-xs">{phase}</span>
        <Badge tone={tone}>{status}</Badge>
      </div>
      <div className="text-xs text-muted">{detail}</div>
    </div>
  );
}

function QuickLink({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link to={to} className="block rounded border p-3 hover:bg-muted/10 transition">
      <div className="font-semibold mb-0.5">{label}</div>
      <div className="text-xs text-muted">{desc}</div>
    </Link>
  );
}
